<template>
  <div class="cif-chip-input">
    <div class="cif-chip-meta">
      <span class="field-label">{{ label }}</span>
      <span v-if="modelValue.length" class="cif-count" :data-testid="testid && `${testid}-count`">
        {{ modelValue.length }} {{ modelValue.length === 1 ? 'CIF' : 'CIFs' }}
        <a v-if="!disabled" href="#" class="cif-clear" @click.prevent="clearAll">Clear all</a>
      </span>
    </div>

    <div class="cif-chip-row">
      <div
        class="cif-chip-box"
        :class="{ 'cif-chip-box--focused': focused, 'cif-chip-box--disabled': disabled }"
        @click="focusInput"
      >
        <v-chip
          v-for="(value, index) in modelValue"
          :key="value"
          size="small"
          :color="value.length > maxLength ? 'error' : undefined"
          :closable="!disabled"
          class="cif-chip"
          :class="{ 'cif-chip--fixed': chipsPerRow > 0 }"
          :style="chipStyle"
          :title="value"
          @click:close="remove(index)"
        >
          <span class="cif-chip-text">{{ value }}</span>
        </v-chip>
        <input
          ref="input"
          :value="text"
          :placeholder="modelValue.length ? '' : placeholder"
          :disabled="disabled"
          class="cif-chip-field"
          :data-testid="testid"
          @input="onInput"
          @paste="onPaste"
          @keydown="onKeydown"
          @focus="focused = true"
          @blur="onBlur"
        />
      </div>
      <!-- the page's Encrypt / Decrypt button, to the right of the box -->
      <div v-if="$slots.action" class="cif-chip-action">
        <slot name="action" />
      </div>
    </div>

    <div v-if="notice" class="cif-hint cif-hint--warn">{{ notice }}</div>
  </div>
</template>

<script>
// [#929] several CIFs as chips: "," / Enter / paste turn text into chips, × removes one, Backspace on an empty
// field removes the last; Enter on an empty field asks the page to run (submit)
const DELIMITERS = /[,\n\r\t]+/;
const CHIP_GAP = 6; // px, same as .cif-chip-box gap

export default {
  name: 'CifChipInput',
  props: {
    modelValue: { type: Array, default: () => [] },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    maxItems: { type: Number, default: 500 },
    maxLength: { type: Number, default: 500 },
    testid: { type: String, default: '' },
    // 0 = chips as wide as their text; n = n equal chips per row, long values cut short (full value on hover)
    chipsPerRow: { type: Number, default: 0 },
  },
  emits: ['update:modelValue', 'update:text', 'submit'],
  data() {
    return {
      text: '',
      focused: false,
      notice: '',
    };
  },
  computed: {
    chipStyle() {
      const n = this.chipsPerRow;
      if (!(n > 0)) return undefined;
      // CHIP_GAP must match the box's gap below
      const width = `calc((100% - ${(n - 1) * CHIP_GAP}px) / ${n})`;
      return { flex: `0 0 ${width}`, maxWidth: width };
    },
  },
  methods: {
    focusInput() {
      if (!this.disabled) this.$refs.input?.focus();
    },
    setText(value) {
      this.text = value;
      this.$emit('update:text', value);
    },
    // adds values that are not chips yet; duplicates and anything past maxItems are left out with a note
    add(values) {
      const next = [...this.modelValue];
      let duplicates = 0;
      let overLimit = 0;
      for (const raw of values) {
        const value = raw.trim();
        if (!value) continue;
        if (next.includes(value)) duplicates++;
        else if (next.length >= this.maxItems) overLimit++;
        else next.push(value);
      }
      const notes = [];
      if (duplicates) notes.push(`${duplicates} duplicate${duplicates > 1 ? 's' : ''} skipped`);
      if (overLimit) notes.push(`${overLimit} not added — at most ${this.maxItems} CIFs`);
      this.notice = notes.join(' · ');
      if (next.length !== this.modelValue.length) this.$emit('update:modelValue', next);
      return next;
    },
    // typed text → chips; returns the full list right away (the parent's v-model updates a tick later)
    commit() {
      const list = this.text.trim() ? this.add(this.text.split(DELIMITERS)) : [...this.modelValue];
      this.setText('');
      return list;
    },
    remove(index) {
      const next = this.modelValue.filter((_, i) => i !== index);
      this.notice = '';
      this.$emit('update:modelValue', next);
    },
    clearAll() {
      this.notice = '';
      this.setText('');
      this.$emit('update:modelValue', []);
      this.focusInput();
    },
    onInput(e) {
      const value = e.target.value;
      if (!DELIMITERS.test(value)) {
        this.setText(value);
        return;
      }
      // everything before the last delimiter becomes chips; what follows stays in the field
      const parts = value.split(DELIMITERS);
      const rest = parts.pop();
      this.add(parts);
      this.setText(rest);
      e.target.value = rest;
    },
    onPaste(e) {
      const pasted = e.clipboardData?.getData('text') || '';
      if (!DELIMITERS.test(pasted)) return; // a single value pastes as normal text
      e.preventDefault();
      const input = e.target;
      const before = this.text.slice(0, input.selectionStart ?? this.text.length);
      const after = this.text.slice(input.selectionEnd ?? this.text.length);
      this.add(`${before}${pasted}${after}`.split(DELIMITERS));
      this.setText('');
    },
    onKeydown(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (this.text.trim()) this.commit();
        else if (this.modelValue.length) this.$emit('submit');
      } else if (e.key === 'Backspace' && !this.text && this.modelValue.length) {
        this.remove(this.modelValue.length - 1);
      }
    },
    onBlur() {
      this.focused = false;
      if (this.text.trim()) this.commit();
    },
  },
};
</script>

<style scoped>
.cif-chip-input {
  min-width: 0;
  flex: 1 1 auto;
}

.cif-chip-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 600;
}

.cif-count {
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  white-space: nowrap;
}

.cif-clear {
  margin-left: 8px;
  font-weight: 400;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* button stays level with the first row of chips when the box grows */
.cif-chip-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.cif-chip-action {
  flex: 0 0 auto;
}

.cif-chip-box {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 56px;
  max-height: 180px;
  overflow-y: auto;
  padding: 10px 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.38);
  border-radius: 4px;
  background: rgb(var(--v-theme-surface));
  cursor: text;
  transition: border-color 0.15s ease;
}

.cif-chip-box:hover {
  border-color: rgba(var(--v-theme-on-surface), 0.87);
}

.cif-chip-box--focused,
.cif-chip-box--focused:hover {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: inset 0 0 0 1px rgb(var(--v-theme-primary));
}

.cif-chip-box--disabled {
  opacity: 0.6;
  cursor: default;
}

.cif-chip {
  max-width: 100%;
}

/* fixed-width chips (chipsPerRow): text fills the chip and ends in "…", the × stays visible */
.cif-chip--fixed :deep(.v-chip__content) {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.cif-chip--fixed .cif-chip-text {
  max-width: 100%;
}

/* long encrypted values: show the start, full value on hover */
.cif-chip-text {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.cif-chip-field {
  flex: 1 1 120px;
  min-width: 120px;
  border: 0;
  outline: 0;
  background: transparent;
  color: rgb(var(--v-theme-on-surface));
  font-size: 1rem;
}

.cif-hint {
  margin-top: 6px;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.cif-hint--warn {
  color: rgb(var(--v-theme-warning));
}
</style>
